import React, { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import '../../styles/content-hub.css';
import useRevealAnimation from '../../hooks/useRevealAnimation';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import { formatContentDate } from './mockContent';
import { ApiRequestError, fetchContentList } from './contentHubApi';
import type { ContentEntry, ContentKind, NewsTopic, ResourceCategory, SupportedLocale } from './types';

type ContentHubPageProps = {
  kind: ContentKind;
};

const PAGE_SIZE = 5;
const NEWS_TOPIC_OPTIONS: Array<{ key: NewsTopic; labelKey: string }> = [
  { key: 'policy', labelKey: 'policy' },
  { key: 'events', labelKey: 'events' },
  { key: 'seminars', labelKey: 'seminars' },
  { key: 'hr-guide', labelKey: 'hrGuide' },
];
const RESOURCE_CATEGORY_OPTIONS: Array<{ key: ResourceCategory; labelKey: string }> = [
  { key: 'talent', labelKey: 'categoryTalent' },
  { key: 'experts', labelKey: 'categoryExperts' },
  { key: 'service-cases', labelKey: 'categoryCases' },
];

const normalizeLanguage = (language?: string | null): SupportedLocale => {
  const normalized = (language ?? '').toLowerCase();
  if (normalized.startsWith('zh')) return 'zh';
  if (normalized.startsWith('ja')) return 'ja';
  return 'en';
};

const ContentHubPage: React.FC<ContentHubPageProps> = ({ kind }) => {
  const { t, i18n } = useTranslation();
  const pageRef = useRef<HTMLDivElement>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState<ContentEntry[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [backendUnavailable, setBackendUnavailable] = useState(false);

  useRevealAnimation(pageRef);

  const isNews = kind === 'news';
  const locale = normalizeLanguage(i18n.resolvedLanguage || i18n.language);
  useDocumentTitle(t(isNews ? 'ContentHub.news.hero.title' : 'ContentHub.resources.hero.title'));
  const requestedPage = Number(searchParams.get('page') ?? '1');
  const requestedTopic = searchParams.get('topic');
  const requestedCategory = searchParams.get('category');
  const selectedTopic: NewsTopic = NEWS_TOPIC_OPTIONS.some((option) => option.key === requestedTopic) ? (requestedTopic as NewsTopic) : 'policy';
  const selectedCategory: ResourceCategory = RESOURCE_CATEGORY_OPTIONS.some((option) => option.key === requestedCategory) ? (requestedCategory as ResourceCategory) : 'talent';
  const currentPage = Math.max(requestedPage, 1);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setBackendUnavailable(false);

      try {
        const result = await fetchContentList(kind, {
          page: currentPage,
          pageSize: PAGE_SIZE,
          lang: locale,
          topic: isNews ? selectedTopic : undefined,
          category: isNews ? undefined : selectedCategory,
        });

        if (!cancelled) {
          setItems(result.items);
          setTotalPages(result.pagination.totalPages);
        }
      } catch (error) {
        if (!cancelled) {
          setItems([]);
          setTotalPages(1);
          setBackendUnavailable(error instanceof ApiRequestError ? error.kind === 'network' : true);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [kind, currentPage, isNews, locale, selectedCategory, selectedTopic]);

  const updateNewsParams = (nextPage: number, nextTopic?: NewsTopic) => {
    const params = new URLSearchParams();
    params.set('topic', nextTopic ?? selectedTopic);
    if (nextPage > 1) params.set('page', String(nextPage));
    setSearchParams(params);
  };

  const updateResourceParams = (nextPage: number, nextCategory?: ResourceCategory) => {
    const params = new URLSearchParams();
    params.set('category', nextCategory ?? selectedCategory);
    if (nextPage > 1) params.set('page', String(nextPage));
    setSearchParams(params);
  };

  const buildDetailLink = (item: ContentEntry) => (item.kind === 'news' ? `/resources/news/${item.slug}` : `/resources/library/${item.slug}`);

  const renderVisual = (item: ContentEntry, className: string) => {
    if (item.coverImageUrl) {
      return (
        <div className={`${className} has-image`} aria-hidden="true">
          <img src={item.coverImageUrl} alt="" className="content-hub-cover-image" loading="lazy" />
        </div>
      );
    }

    return (
      <div className={`${className} tone-${item.coverTone}`} aria-hidden="true">
        <div className="content-hub-card-orb" />
        <div className="content-hub-card-lines">
          <span />
          <span />
          <span className="is-short" />
        </div>
      </div>
    );
  };

  return (
    <div className="content-hub-page" ref={pageRef}>
      <section className="section-container content-hub-page-shell">
        <div className="content-hub-page-header reveal">
          <div className="content-hub-page-heading">
            <span className="content-hub-kicker">{t(`ContentHub.${kind}.hero.kicker`)}</span>
            <h1 className="content-hub-page-title">{t(`ContentHub.${kind}.hero.title`)}</h1>
            <p className="content-hub-page-description">{t(`ContentHub.${kind}.hero.description`)}</p>
          </div>
        </div>

        {isNews ? (
          <div className="content-hub-filter-bar reveal delay-1">
            {NEWS_TOPIC_OPTIONS.map((option) => (
              <button type="button" key={option.key} className={`content-hub-filter-pill${selectedTopic === option.key ? ' is-active' : ''}`} onClick={() => updateNewsParams(1, option.key)}>
                {t(`ContentHub.news.filters.${option.labelKey}`)}
              </button>
            ))}
          </div>
        ) : (
          <div className="content-hub-filter-bar reveal delay-1">
            {RESOURCE_CATEGORY_OPTIONS.map((option) => (
              <button type="button" key={option.key} className={`content-hub-filter-pill${selectedCategory === option.key ? ' is-active' : ''}`} onClick={() => updateResourceParams(1, option.key)}>
                {t(`ContentHub.common.${option.labelKey}`)}
              </button>
            ))}
          </div>
        )}

        {loading ? (
          <div className="content-hub-empty reveal delay-2">{t('ContentHub.common.loading')}</div>
        ) : backendUnavailable ? (
          <div className="content-hub-empty reveal delay-2">{t('ContentHub.common.backendUnavailable')}</div>
        ) : (
          <>
            <div className={`content-hub-list${isNews ? ' is-news' : ' is-resource'}`}>
              {items.length > 0 ? items.map((item, index) => (
                <article key={item.id} className={`content-hub-card reveal${isNews ? ' is-news' : ' is-resource'}`} style={{ transitionDelay: `${0.08 * (index + 1)}s` }}>
                  <Link to={buildDetailLink(item)} className="content-hub-card-link">
                    {renderVisual(item, 'content-hub-card-visual')}
                    <div className="content-hub-card-copy">
                      <div className="content-hub-card-meta">
                        <span className="content-hub-card-badge">{item.badge}</span>
                        <span className="content-hub-card-date">{formatContentDate(item.date, i18n.resolvedLanguage)}</span>
                      </div>
                      <h2>{item.title}</h2>
                      <p>{item.summary}</p>
                      <span className="content-hub-card-cta">{t('ContentHub.common.readMore')} -&gt;</span>
                    </div>
                  </Link>
                </article>
              )) : <div className="content-hub-empty reveal delay-2">{t('ContentHub.common.noData')}</div>}
            </div>

            {items.length > 0 ? (
              <div className="content-hub-pagination reveal delay-2">
                <button type="button" className="content-hub-page-button" onClick={() => (isNews ? updateNewsParams(currentPage - 1) : updateResourceParams(currentPage - 1))} disabled={currentPage === 1}>
                  {t('ContentHub.common.prev')}
                </button>

                <div className="content-hub-page-list">
                  {Array.from({ length: totalPages }, (_, index) => {
                    const pageNumber = index + 1;
                    return (
                      <button
                        type="button"
                        key={pageNumber}
                        className={`content-hub-page-chip${pageNumber === currentPage ? ' is-active' : ''}`}
                        onClick={() => (isNews ? updateNewsParams(pageNumber) : updateResourceParams(pageNumber))}
                      >
                        {pageNumber}
                      </button>
                    );
                  })}
                </div>

                <button type="button" className="content-hub-page-button" onClick={() => (isNews ? updateNewsParams(currentPage + 1) : updateResourceParams(currentPage + 1))} disabled={currentPage === totalPages}>
                  {t('ContentHub.common.next')}
                </button>
              </div>
            ) : null}
          </>
        )}
      </section>
    </div>
  );
};

export default ContentHubPage;

